/**
 * Self-improvement (generative-network) REST client. GENERATIVE-NETWORKS.md §6a/§6b.
 *
 *   GET  /networks/{id}/manifest-health         → per-node stats matrix + satisfaction
 *   POST /networks/{id}/manifest-rating         → user thumbs up/down on a run outcome
 *   POST /networks/{id}/manifest-optimize       → the slow-loop suggestions (a diff proposal)
 *   POST /networks/{id}/manifest-revise-prompt  → frame a revise prompt from selected ids
 *
 * The revise prompt returned by the last call is what the user carries into the Brain2
 * revise route (startBrainDeployment with { generative: true }) — the network improves
 * itself through the SAME propose→approve→effect pipeline, never a silent edit.
 */
import { getRestApiBearerToken } from './apiToken';

const API_BASE_URL = import.meta.env.VITE_API_BASE_URL || 'https://api.stepflow.dev';

async function authed(path, init = {}) {
  const token = getRestApiBearerToken();
  if (!token) throw new Error('Not authenticated');
  const res = await fetch(`${API_BASE_URL}${path}`, {
    ...init,
    headers: { Authorization: `Bearer ${token}`, 'Content-Type': 'application/json', ...(init.headers || {}) },
  });
  const data = await res.json().catch(() => ({}));
  if (!res.ok) throw new Error(data.error || data.message || `HTTP ${res.status}`);
  return data;
}

const nid = (n) => encodeURIComponent(String(n || '').trim());

/** Per-node health matrix + network satisfaction (read-only report). */
export function getManifestHealth(networkId) {
  return authed(`/networks/${nid(networkId)}/manifest-health`);
}

/** Record the user's thumbs up/down on a run outcome (the quality anchor). */
export function rateNetworkRun(networkId, thumbsUp) {
  return authed(`/networks/${nid(networkId)}/manifest-rating`, {
    method: 'POST', body: JSON.stringify({ thumbsUp: !!thumbsUp }),
  });
}

/** The slow-loop improvement suggestions (proposal diff — nothing applied). */
export function getManifestSuggestions(networkId) {
  return authed(`/networks/${nid(networkId)}/manifest-optimize`, {
    method: 'POST', body: JSON.stringify({}),
  });
}

/** Frame a revise prompt from the user-SELECTED suggestion ids (omit = all). The
 *  returned ``prompt`` is carried into the Brain2 revise route. */
export function composeRevisePrompt(networkId, selectedIds) {
  return authed(`/networks/${nid(networkId)}/manifest-revise-prompt`, {
    method: 'POST',
    body: JSON.stringify(selectedIds != null ? { selectedIds } : {}),
  });
}
